import React, { Suspense, useEffect, useState, useRef } from "react";
import { Button, ButtonLink, DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger, Input, Modal, ModalBody, ModalFooter, ModalHeader, toast } from "@clarkinc/zest-design-system/ui";
import { User } from "~/common/Store/models/User";
import useIsMounted from "~/common/useIsMounted";
import ValidationError from "../Pricing/AddToCart/ValidationError";
import { createVgSelectionError } from "../Pricing/AddToCart/validations";
import { PricingProduct } from "~/ProductDetails/Pricing/PricingProduct";
import fetchCreateNewWishList from "./fetchCreateNewWishList";
import fetchAddItemToWishList from "./fetchAddItemToWishList";
import { addToWishlistClick } from "~/userAnalytics";
import CustomToast from "./CustomToast";
interface Props {
  user: User;
  handleVgSelectionError: (validationError: ValidationError) => void;
  product: PricingProduct;
  addToCartQuantity: string;
}
interface WishListItem {
  listName?: string;
  num?: number;
}
const WishListButton = ({
  user,
  handleVgSelectionError,
  product,
  addToCartQuantity
}: Props) => {
  const isMounted = useIsMounted();
  const [currentUrl, setCurrentUrl] = useState("");
  const [wishLists, setWishLists] = useState<WishListItem[]>([]);
  const [newWishListName, setNewWishListName] = useState("");
  const [validationMessage, setValidationMessage] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    setCurrentUrl(window.location.pathname + window.location.search);
  }, [currentUrl]);
  useEffect(() => {
    const wishLists = [...user.wishLists].sort((a, b) => (a.listName?.toUpperCase() ?? "") > (b.listName?.toUpperCase() ?? "") ? 1 : -1);
    setWishLists(wishLists);
  }, [user.wishLists]);
  const handleNewWishListNameChange = (value: string) => {
    setValidationMessage("");
    setNewWishListName(value);
  };
  const handleOpenModal = (event: React.SyntheticEvent) => {
    event.preventDefault();
    setValidationMessage("");
    setNewWishListName("");
    setIsModalOpen(true);
  };
  const createAddToWishListNotification = async (wishListName: string, wishListId: number | undefined, addToWishListData: string) => {
    toast(<CustomToast body={product.header} title={addToWishListData.length > 1 ? `${addToCartQuantity} item${Number(addToCartQuantity) > 1 ? "s" : ""} added to your ${wishListName} wish list` : `Item already exists in your ${wishListName} wish list`} buttonLinkText="View Wish List" imageUrl={product.productImages?.find(x => x.isPrimaryDetailImage)?.thumbnail ?? ""} buttonLinkUrl={`/wishlist.html?wlindex=${String(wishListId)}`} />);
  };
  const addItemToWishList = async (wishListName: string | undefined, wishListId: number | undefined) => {
    if (wishListId && wishListName && Number(addToCartQuantity)) {
      try {
        await fetchAddItemToWishList(user.index ?? "", wishListId, product.itemNumber, addToCartQuantity).then(response => createAddToWishListNotification(wishListName, wishListId, response));
        addToWishlistClick(product.itemNumber);
      } catch (error) {
        toast.error(<span className="flex flex-col gap-1">
            <p className="font-bold">Error creating wish list</p>
            <p className="whitespace-nowrap">
              Please try again.{" "}
              <a href="/ask.html" className="underline">
                Chat
              </a>{" "}
              with us if you&apos;re still having issues.
            </p>
          </span>);
      }
    }
  };
  const handleAddItemToWishList = (event: React.SyntheticEvent, wishListName: string | undefined, wishListId: number | undefined) => {
    event.preventDefault();
    menuButtonRef.current?.focus();
    addItemToWishList(wishListName, wishListId);
  };
  const addWishListToCollection = (wishListId: number | undefined) => {
    const newWishLists = [...wishLists];
    newWishLists.push({
      listName: newWishListName,
      num: wishListId
    });
    setWishLists(newWishLists);
    return wishListId;
  };
  const newWishListNameIsInvalid = (newWishListName: string) => {
    const regex = new RegExp(/^\S.*/);
    return !regex.test(newWishListName);
  };
  const handleCreateWishlistSubmit = async (wishListName: string) => {
    if (newWishListNameIsInvalid(newWishListName)) {
      setValidationMessage("Name can't be empty or start with a space.");
      return;
    }
    setIsModalOpen(false);
    try {
      const data = new FormData();
      data.append("method", "createWishList");
      data.append("newWishListName", wishListName);
      await fetchCreateNewWishList(wishListName, user.index ?? "").then(wishListId => addWishListToCollection(wishListId)).then(wishListId => addItemToWishList(wishListName, wishListId)).then();
    } catch (error) {
      if (typeof error === "string") {
        toast.error(<>
            <p className="font-bold">Error creating wish list</p>
            <p>{error}</p>
          </>);
      }
    }
  };
  const isVirtualGroupMaster = product.groupingAttributes.isGroupMaster && product.groupingAttributes.isVirtual;
  if (isVirtualGroupMaster) {
    return <div className={`w-full leading-none ml-5 ${user.orderGuideEnabled ? "mr-2 md:mr-2.5" : "mr-5"}`}>
        <Button theme="wss" size="small" color="gray" full className="whitespace-nowrap font-proxima" onClick={() => handleVgSelectionError(createVgSelectionError())} data-testid="VG-wish-list-button">
          {" "}
          Add to Wish List{" "}
        </Button>
      </div>;
  }
  return <>
      {isMounted && <Suspense fallback={null}>
          <Modal theme="wss" className="w-[504px]" open={isModalOpen} onOpenChange={setIsModalOpen}>
            <ModalHeader>Create a new wish list</ModalHeader>
            <ModalBody>
              <form>
                <Input label="Wish List Name" className="w-[264px]" maxLength={40} value={newWishListName} autoComplete="off" onValueChange={handleNewWishListNameChange} aria-required id="newWishListName" name="newWishListName" data-testid="new-wish-list-name-input" hasError={validationMessage !== ""} errorMessages={validationMessage} />
                <p id="result" />
              </form>
            </ModalBody>
            <ModalFooter>
              <Button theme="wss" color="white" type="button" className="font-proxima font-semibold" onClick={() => setIsModalOpen(false)} data-testid="cancel-create-wishlist-button">
                Cancel
              </Button>
              <Button theme="wss" color="green" className="font-proxima font-semibold" aria-label="Create Wish List" data-testid="create-wishlist-button" type="submit" onClick={() => handleCreateWishlistSubmit(newWishListName)}>
                Create Wish List
              </Button>
            </ModalFooter>
          </Modal>
        </Suspense>}
      <div className={`w-full leading-none ml-5 ${user.orderGuideEnabled ? "mr-2 md:mr-2.5" : "mr-5"}`}>
        {user.registered ? <DropdownMenu data-testid="wish_list_dropdown_container">
            <DropdownMenuTrigger theme="wss" id="menu-button" ref={menuButtonRef} data-testid="wish-list-button" color="gray" full size="small" className="whitespace-nowrap font-proxima">
              Add to Wish List
            </DropdownMenuTrigger>
            <DropdownMenuContent full theme="wss" data-testid="wish_list_dropdown_content">
              {wishLists?.length > 0 && <>
                  {wishLists?.map(wishList => wishList && <DropdownMenuItem key={wishList.listName} onClick={event => handleAddItemToWishList(event, wishList.listName, wishList.num)} id={`wishlist_item_${wishList.num}`} data-testid={`add-to-wish-list-${wishList.listName}`}>
                          {wishList.listName}
                        </DropdownMenuItem>)}
                  <DropdownMenuSeparator />
                </>}
              <DropdownMenuItem onClick={handleOpenModal} data-testid="new-wish-list-button" id="wishlist_last_item">
                New Wish List
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu> : <ButtonLink theme="wss" size="small" color="gray" full className="whitespace-nowrap font-proxima" href={`/myaccount?target_url=${currentUrl}`} data-testid="logged-out-wish-list-button">
            {" "}
            Add to Wish List{" "}
          </ButtonLink>}
      </div>
    </>;
};
export default WishListButton;